/**
 * Internationalization (i18n) provider and hook.
 * Loads translations from JSON resource files and exposes a
 * `t(key, params)` function for looking up translated strings.
 *
 * @module dataProviders/I18nProvider
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/** @type {React.Context} */
const I18nContext = createContext(null);

/**
 * Provides i18n translations to child components.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components.
 * @returns {React.ReactElement}
 */
export function I18nProvider({ children }) {
  const [locale, setLocale]             = useState('en');
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    loadTranslations(locale).then(setTranslations);
  }, [locale]);

  /**
   * Translate a key, optionally replacing {placeholder} tokens.
   *
   * @param {string} key         - Dot-notation key (e.g. "nav.help").
   * @param {object} [params={}] - Replacement values for {placeholder} tokens.
   * @returns {string} The translated string, or the key itself if not found.
   */
  const t = useCallback((key, params = {}) => {
    let text = translations[key] || key;
    for (const [param, value] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), value);
    }
    return text;
  }, [translations]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook for accessing the i18n context.
 *
 * @returns {{ locale: string, setLocale: Function, t: Function }}
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

/**
 * Dynamically load a locale's translation JSON file.
 *
 * @param {string} locale - Locale code (e.g. "en").
 * @returns {Promise<object>} The translations object.
 */
async function loadTranslations(locale) {
  try {
    const module = await import(`../data/i18n/${locale}.json`);
    return module.default || module;
  } catch {
    console.warn(`Failed to load translations for locale "${locale}", falling back to empty.`);
    return {};
  }
}
